import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Box } from 'lucide-react';
import { usePageConfig } from '../../contexts/PageConfigContext';
// import { Permissions } from '../../constants/permissions'; // TODO: Add permissions for Pallet Types
import { palletTypesApi, PalletType, CreatePalletTypeRequest } from '../../services/api';
// import { usePermission } from '../../hooks/usePermission'; // TODO: Use permissions
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';

export const PalletTypesPage: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  // const canView = usePermission(Permissions.Logistics.ViewPalletTypes); // Placeholder
  // const canCreate = usePermission(Permissions.Logistics.CreatePalletTypes); // Placeholder
  const canView = true; // Temporary
  const canCreate = true; // Temporary
  const canEdit = true;
  const canDelete = true;
  
  const [palletTypes, setPalletTypes] = useState<PalletType[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'model'>('general');

  const [formData, setFormData] = useState<CreatePalletTypeRequest>({
    name: '',
    code: '',
    lengthCm: 120,
    widthCm: 80,
    baseHeightCm: 14.4,
    maxHeightCm: 145,
    maxWeightKg: 1000
  });

  // State to track if the current editing item has a model (for UI feedback)
  const [currentModelPath, setCurrentModelPath] = useState<string | undefined>(undefined);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0 && editingId) {
       const file = e.target.files[0];
       
       if (!file.name.toLowerCase().endsWith('.glb') && !file.name.toLowerCase().endsWith('.gltf')) {
           alert("Solo se permiten archivos .glb o .gltf");
           return;
       }
       
       try {
           setIsUploading(true);
           const result = await palletTypesApi.uploadModel(editingId, file);
           alert("Modelo 3D subido con éxito");
           setCurrentModelPath(result.path);
           loadPalletTypes();
       } catch (err: any) {
           console.error(err);
           alert("Error al subir modelo: " + (err.message || "Error desconocido"));
       } finally {
           setIsUploading(false);
       }
    }
  };

  useEffect(() => {
    setPageConfig({
      title: 'Tipos de Palet',
      actions: canCreate ? [
        {
          label: 'Nuevo Tipo de Palet',
          icon: Plus,
          onClick: () => {
            setEditingId(null);
            setFormData({ 
              name: '', 
              code: '', 
              lengthCm: 120,
              widthCm: 80,
              baseHeightCm: 14.4,
              maxHeightCm: 145,
              maxWeightKg: 1000
            });
            setCurrentModelPath(undefined);
            setActiveTab('general');
            setShowForm(true);
          },
          variant: 'primary' as const
        }
      ] : []
    });
  }, [setPageConfig, canCreate]);

  const loadPalletTypes = async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const data = await palletTypesApi.getAll();
      setPalletTypes(data);
    } catch (error) {
      alert('Error al cargar tipos de palet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPalletTypes();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await palletTypesApi.update(editingId, formData);
        alert('Tipo de palet actualizado');
      } else {
        await palletTypesApi.create(formData);
        alert('Tipo de palet creado');
      }
      setShowForm(false);
      loadPalletTypes();
    } catch (error: any) {
      alert(error.message || 'Error al guardar tipo de palet');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este tipo de palet?')) return;
    try {
      await palletTypesApi.delete(id);
      loadPalletTypes();
    } catch (error: any) {
      alert(error.message || 'Error al eliminar');
    }
  };

  const handleEdit = (pallet: PalletType) => {
    setEditingId(pallet.id);
    setFormData({
      name: pallet.name,
      code: pallet.code,
      lengthCm: pallet.lengthCm,
      widthCm: pallet.widthCm,
      baseHeightCm: pallet.baseHeightCm || 14.4,
      maxHeightCm: pallet.maxHeightCm,
      maxWeightKg: pallet.maxWeightKg
    });
    setCurrentModelPath(pallet.model3DPath);
    setActiveTab('general');
    setShowForm(true);
  };

  const filteredPalletTypes = palletTypes.filter(pt => 
    pt.name.toLowerCase().includes(search.toLowerCase()) ||
    pt.code.toLowerCase().includes(search.toLowerCase())
  );

  if (!canView) return <div className="p-6">No tienes permisos para ver tipos de palet</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o código..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredPalletTypes.map((palletType) => (
            <Card key={palletType.id} className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold">{palletType.name}</h3>
                  <p className="text-sm text-muted-foreground">Código: {palletType.code}</p>
                  <p className="text-sm">
                    Dimensiones: {palletType.lengthCm}×{palletType.widthCm}×{palletType.maxHeightCm} cm
                  </p>
                  <p className="text-sm">Peso máx: {palletType.maxWeightKg} kg</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded ${palletType.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {palletType.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div className="flex justify-end mt-4 gap-2">
                 {canEdit && (
                    <Button variant="outline" size="sm" onClick={() => handleEdit(palletType)}>
                      Editar
                    </Button>
                 )}
                 {canDelete && (
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(palletType.id)}>
                      Eliminar
                    </Button>
                 )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Tipo de Palet' : 'Nuevo Tipo de Palet'}</DialogTitle>
            <DialogDescription>
              Defina las características del tipo de palet
            </DialogDescription>
          </DialogHeader>

          <div className="flex border-b mb-4">
            <button
              type="button"
              className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'general' 
                  ? 'border-green-600 text-green-700' 
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
              onClick={() => setActiveTab('general')}
            >
              General
            </button>
            {editingId && (
              <button
                type="button"
                className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'model' 
                    ? 'border-green-600 text-green-700' 
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
                onClick={() => setActiveTab('model')}
              >
                Modelo 3D
              </button>
            )}
          </div>

          {activeTab === 'general' ? (
            <form id="pallet-form" onSubmit={handleSubmit}>
                <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                    <Label htmlFor="name">Nombre *</Label>
                    <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                    />
                    </div>
                    <div>
                    <Label htmlFor="code">Código *</Label>
                    <Input
                        id="code"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        required
                        placeholder="EUR"
                    />
                    </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                    <div>
                    <Label htmlFor="lengthCm">Largo (cm) *</Label>
                    <Input
                        id="lengthCm"
                        type="number"
                        value={formData.lengthCm}
                        onChange={(e) => setFormData({ ...formData, lengthCm: Number(e.target.value) })}
                        required
                    />
                    </div>
                    <div>
                    <Label htmlFor="widthCm">Ancho (cm) *</Label>
                    <Input
                        id="widthCm"
                        type="number"
                        value={formData.widthCm}
                        onChange={(e) => setFormData({ ...formData, widthCm: Number(e.target.value) })}
                        required
                    />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                    <Label htmlFor="baseHeightCm">Altura Base (cm) *</Label>
                    <Input
                        id="baseHeightCm"
                        type="number"
                        step="0.1"
                        value={formData.baseHeightCm}
                        onChange={(e) => setFormData({ ...formData, baseHeightCm: Number(e.target.value) })}
                        required
                    />
                    </div>
                    <div>
                    <Label htmlFor="maxWeightKg">Peso máx (kg) *</Label>
                    <Input
                        id="maxWeightKg"
                        type="number"
                        value={formData.maxWeightKg}
                        onChange={(e) => setFormData({ ...formData, maxWeightKg: Number(e.target.value) })}
                        required
                    />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                    <Label htmlFor="maxHeightCm">Altura Carga Máx (cm) *</Label>
                    <Input
                        id="maxHeightCm"
                        type="number"
                        value={formData.maxHeightCm}
                        onChange={(e) => setFormData({ ...formData, maxHeightCm: Number(e.target.value) })}
                        required
                    />
                    </div>
                </div>
                </div>
            </form>
          ) : (
            <div className="space-y-6 py-4">
                <div className="bg-slate-50 p-4 rounded-lg flex flex-col items-center justify-center border border-dashed border-slate-300 min-h-[150px]">
                    {currentModelPath ? (
                        <div className="text-center">
                            <Box className="h-12 w-12 text-green-600 mx-auto mb-2" />
                            <p className="text-sm font-medium text-green-700">Modelo Cargado</p>
                            <p className="text-xs text-slate-500 mt-1 truncate max-w-[200px]">{currentModelPath}</p>
                        </div>
                    ) : (
                        <div className="text-center text-slate-500">
                            <Box className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>No hay modelo 3D asignado</p>
                        </div>
                    )}
                </div>

                <div className="flex justify-center">
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        className="hidden" 
                        accept=".glb,.gltf" 
                        onChange={handleFileChange} 
                    />
                    <Button 
                        type="button" 
                        variant="secondary"
                        onClick={handleUploadClick}
                        disabled={isUploading}
                    >
                        {isUploading ? 'Subiendo...' : currentModelPath ? 'Reemplazar Modelo' : 'Subir Modelo (.glb)'}
                    </Button>
                </div>
                
                <p className="text-xs text-center text-slate-500">
                    Sube un archivo .glb o .gltf para visualizar este palet en el constructor 3D.
                </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
              {activeTab === 'model' ? 'Cerrar' : 'Cancelar'}
            </Button>
            {activeTab === 'general' && (
                <Button type="submit" form="pallet-form">Guardar</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
